import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { 
  Scan, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CreditCard,
  Clock,
  User,
  Activity,
  Search,
  Hash,
  Maximize,
  Minimize,
  Phone,
  Mail
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CheckIn() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('code'); // 'code' or 'search'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const inputRef = useRef(null);
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    if (mode === 'code') inputRef.current?.focus();
    else searchRef.current?.focus();
  }, [mode]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (result || error) {
      const timer = setTimeout(() => {
        setResult(null);
        setError('');
        setCode('');
        setSearchQuery('');
        setSearchResults([]);
        if (mode === 'code') inputRef.current?.focus();
        else searchRef.current?.focus();
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [result, error, mode]);

  // Debounced search
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/api/clients/search?q=${encodeURIComponent(value)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, []);

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim() || loading) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/api/checkin`, { code: code.trim() }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Código no válido');
    } finally {
      setLoading(false);
    }
  };

  const handleClientCheckin = async (clientId) => {
    setLoading(true);
    setError('');
    setResult(null);
    setSearchResults([]);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/api/checkin/client/${clientId}`, {}, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar entrada');
    } finally {
      setLoading(false);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const getStatusConfig = () => {
    if (!result) return null;
    if (result.status === 'expired') {
      return {
        icon: XCircle,
        color: '#ef4444',
        colorLight: '#f87171',
        title: 'ACCESO DENEGADO',
        subtitle: 'Membresía Vencida',
        iconClass: 'nova-checkin-icon-danger',
        bgClass: 'nova-checkin-danger'
      };
    } else if (result.status === 'expiring') {
      return {
        icon: AlertTriangle,
        color: '#f59e0b',
        colorLight: '#fbbf24',
        title: 'ACCESO PERMITIDO',
        subtitle: 'Membresía Por Vencer',
        iconClass: 'nova-checkin-icon-warning',
        bgClass: 'nova-checkin-warning'
      };
    } else {
      return {
        icon: CheckCircle2,
        color: '#22c55e',
        colorLight: '#4ade80',
        title: 'ACCESO PERMITIDO',
        subtitle: 'Membresía Activa',
        iconClass: 'nova-checkin-icon-success',
        bgClass: 'nova-checkin-success'
      };
    }
  };

  const getMembershipStatusForClient = (client) => {
    if (!client.membership_end) return { label: 'Sin membresía', color: '#6e6e73', dot: 'bg-gray-500' };
    const daysLeft = Math.ceil((new Date(client.membership_end) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { label: 'Vencido', color: '#ef4444', dot: 'bg-red-500' };
    if (daysLeft <= 7) return { label: `${daysLeft}d restantes`, color: '#f59e0b', dot: 'bg-amber-500' };
    return { label: 'Activo', color: '#22c55e', dot: 'bg-emerald-500' };
  };

  const statusConfig = getStatusConfig();

  return (
    <div data-testid="checkin-page" className="min-h-screen nova-checkin-bg flex flex-col relative overflow-hidden">
      {/* Grid pattern */}
      <div className="absolute inset-0 nova-grid-pattern opacity-30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/8 to-transparent rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 lg:p-6 flex items-center justify-between">
        <Link 
          to="/dashboard" 
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[#a1a1a6] hover:text-white hover:bg-white/5 transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Dashboard</span>
        </Link>
        <div className="flex items-center gap-4">
          {/* Fullscreen toggle */}
          <button
            onClick={toggleFullscreen}
            data-testid="fullscreen-toggle"
            className="p-2.5 rounded-xl text-[#6e6e73] hover:text-white hover:bg-white/5 transition-all"
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          >
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </button>
          <div className="text-right">
            <p className="text-2xl font-semibold text-white tabular-nums tracking-tight">
              {currentTime.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
            </p>
            <p className="text-xs text-[#6e6e73] mt-0.5">
              {currentTime.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          <div className="w-px h-10 bg-white/[0.08]" />
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <span className="text-white font-bold text-sm">N</span>
            </div>
            <div>
              <span className="text-white font-medium text-sm">{user?.business_name || 'Nova'}</span>
              <span className="block text-[10px] text-[#6e6e73] uppercase tracking-wider">Recepción</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 relative z-10">
        <AnimatePresence mode="wait">
          {!result && !error ? (
            <motion.div
              key="input"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-2xl"
            >
              {/* Mode toggle */}
              <div className="flex justify-center mb-10">
                <div className="flex bg-[#111113] border border-white/[0.06] rounded-2xl p-1.5">
                  <button
                    onClick={() => { setMode('code'); setSearchQuery(''); setSearchResults([]); }}
                    data-testid="mode-code"
                    className={`flex items-center gap-2.5 px-6 py-3 rounded-xl text-sm font-medium transition-all ${
                      mode === 'code'
                        ? 'bg-[var(--nova-primary)] text-white shadow-lg shadow-indigo-500/25'
                        : 'text-[#6e6e73] hover:text-white'
                    }`}
                  >
                    <Hash className="w-4 h-4" />
                    Código
                  </button>
                  <button
                    onClick={() => { setMode('search'); setCode(''); }}
                    data-testid="mode-search"
                    className={`flex items-center gap-2.5 px-6 py-3 rounded-xl text-sm font-medium transition-all ${
                      mode === 'search'
                        ? 'bg-[var(--nova-primary)] text-white shadow-lg shadow-indigo-500/25'
                        : 'text-[#6e6e73] hover:text-white'
                    }`}
                  >
                    <Search className="w-4 h-4" />
                    Buscar por nombre
                  </button>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {mode === 'code' ? (
                  <motion.div
                    key="code-mode"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                    className="text-center"
                  >
                    <motion.div 
                      className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-[#161618] border border-white/[0.08] flex items-center justify-center shadow-2xl"
                      animate={{ boxShadow: ['0 0 0 rgba(99,102,241,0)', '0 0 50px rgba(99,102,241,0.12)', '0 0 0 rgba(99,102,241,0)'] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    >
                      <Scan className="w-10 h-10 text-indigo-400" />
                    </motion.div>
                    <h1 className="text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight">Check-in</h1>
                    <p className="text-lg text-[#6e6e73] mb-10">Ingresa el código de acceso de 6 dígitos</p>

                    <form onSubmit={handleCodeSubmit} className="space-y-8">
                      <div>
                        <input
                          ref={inputRef}
                          type="text"
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          data-testid="checkin-input"
                          className="nova-checkin-input w-full"
                          placeholder="000000"
                          maxLength={6}
                          autoComplete="off"
                          disabled={loading}
                        />
                        <div className="flex justify-center gap-3 mt-5">
                          {[...Array(6)].map((_, i) => (
                            <div key={i} className={`nova-checkin-digit ${i < code.length ? 'nova-checkin-digit-active' : ''}`} />
                          ))}
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={code.length !== 6 || loading}
                        data-testid="checkin-submit"
                        className="nova-btn nova-btn-primary px-14 py-4 text-lg font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <span className="flex items-center gap-3">
                            <motion.div 
                              className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                              animate={{ rotate: 360 }}
                              transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                            />
                            Verificando...
                          </span>
                        ) : 'Verificar Acceso'}
                      </button>
                    </form>
                  </motion.div>
                ) : (
                  <motion.div
                    key="search-mode"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="text-center"
                  >
                    <motion.div 
                      className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-[#161618] border border-white/[0.08] flex items-center justify-center shadow-2xl"
                      animate={{ boxShadow: ['0 0 0 rgba(99,102,241,0)', '0 0 50px rgba(99,102,241,0.12)', '0 0 0 rgba(99,102,241,0)'] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    >
                      <User className="w-10 h-10 text-indigo-400" />
                    </motion.div>
                    <h1 className="text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight">Buscar Cliente</h1>
                    <p className="text-lg text-[#6e6e73] mb-10">Busca por nombre para registro rápido</p>

                    {/* Search input */}
                    <div className="relative max-w-xl mx-auto mb-6">
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-[#6e6e73]">
                        <Search className="w-5 h-5" />
                      </div>
                      <input
                        ref={searchRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        data-testid="checkin-search-input"
                        className="w-full bg-[#111113] border border-white/[0.08] rounded-2xl pl-14 pr-6 py-5 text-xl text-white placeholder-[#48484d] focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/25 transition-all"
                        placeholder="Escribe el nombre del cliente..."
                        autoComplete="off"
                      />
                      {searchLoading && (
                        <div className="absolute right-5 top-1/2 -translate-y-1/2">
                          <motion.div 
                            className="w-5 h-5 border-2 border-indigo-500/30 border-t-indigo-400 rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Search Results */}
                    <AnimatePresence>
                      {searchResults.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          className="max-w-xl mx-auto space-y-2 max-h-[400px] overflow-y-auto"
                        >
                          {searchResults.map((client, i) => {
                            const memberStatus = getMembershipStatusForClient(client);
                            return (
                              <motion.button
                                key={client.id}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.04 }}
                                onClick={() => handleClientCheckin(client.id)}
                                disabled={loading}
                                data-testid={`search-result-${i}`}
                                className="w-full flex items-center gap-4 p-5 bg-[#111113] border border-white/[0.06] rounded-2xl hover:bg-[#161618] hover:border-indigo-500/20 transition-all text-left group"
                              >
                                <div 
                                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-bold text-white shrink-0"
                                  style={{ background: `linear-gradient(135deg, ${memberStatus.color}40 0%, ${memberStatus.color}20 100%)` }}
                                >
                                  {client.name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-3">
                                    <p className="text-lg font-semibold text-white truncate">{client.name}</p>
                                    <span 
                                      className="text-xs font-medium px-2.5 py-1 rounded-lg shrink-0"
                                      style={{ 
                                        backgroundColor: `${memberStatus.color}15`,
                                        color: memberStatus.color
                                      }}
                                    >
                                      {memberStatus.label}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 mt-1.5 text-sm text-[#6e6e73]">
                                    {client.email && (
                                      <span className="flex items-center gap-1 truncate">
                                        <Mail className="w-3.5 h-3.5" />
                                        {client.email}
                                      </span>
                                    )}
                                    {client.phone && (
                                      <span className="flex items-center gap-1">
                                        <Phone className="w-3.5 h-3.5" />
                                        {client.phone}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="px-4 py-2 rounded-xl bg-indigo-500/10 text-indigo-400 text-sm font-medium border border-indigo-500/20">
                                    Check-in
                                  </div>
                                </div>
                              </motion.button>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {searchQuery.length >= 2 && !searchLoading && searchResults.length === 0 && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-6 text-[#6e6e73]"
                      >
                        <p className="text-base">No se encontraron clientes con "{searchQuery}"</p>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ) : error ? (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="text-center nova-checkin-danger py-12"
            >
              <div className="nova-checkin-icon nova-checkin-icon-danger">
                <XCircle className="w-16 h-16 text-red-400" />
              </div>
              <h2 className="text-4xl font-bold text-red-400 tracking-tight">ERROR</h2>
              <p className="text-lg text-[#a1a1a6] mt-3">{error}</p>
              <p className="text-sm text-[#48484d] mt-8">Reiniciando en unos segundos...</p>
            </motion.div>
          ) : result ? (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className={`text-center w-full max-w-xl ${statusConfig.bgClass} py-8`}
            >
              <div className={`nova-checkin-icon ${statusConfig.iconClass}`}>
                <statusConfig.icon className="w-16 h-16" style={{ color: statusConfig.colorLight }} />
              </div>
              <motion.h2 
                className="text-4xl font-bold tracking-tight"
                style={{ color: statusConfig.colorLight }}
                initial={{ y: 20 }}
                animate={{ y: 0 }}
              >
                {statusConfig.title}
              </motion.h2>
              <p className="text-lg text-[#a1a1a6] mt-2">{statusConfig.subtitle}</p>
              
              <motion.div 
                className="mt-10 p-8 nova-card bg-[#161618]/90 text-left"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-center gap-5 mb-8 pb-6 border-b border-white/[0.06]">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold text-white shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${statusConfig.color} 0%, ${statusConfig.color}99 100%)` }}
                  >
                    {result.client.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-white">{result.client.name}</h3>
                    <p className="text-[#6e6e73] flex items-center gap-2 mt-1">
                      <User className="w-4 h-4" />
                      {result.client.email || result.client.phone || 'Cliente registrado'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-[#111113] border border-white/[0.04]">
                    <div className="flex items-center gap-2 text-[#6e6e73] mb-2">
                      <CreditCard className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase tracking-wider">Estado</span>
                    </div>
                    <span 
                      className="nova-badge"
                      style={{ 
                        background: `linear-gradient(135deg, ${statusConfig.color}20 0%, ${statusConfig.color}10 100%)`,
                        color: statusConfig.colorLight,
                        border: `1px solid ${statusConfig.color}30`
                      }}
                    >
                      {result.status === 'active' ? 'ACTIVO' : result.status === 'expiring' ? 'POR VENCER' : 'VENCIDO'}
                    </span>
                  </div>
                  <div className="p-4 rounded-xl bg-[#111113] border border-white/[0.04]">
                    <div className="flex items-center gap-2 text-[#6e6e73] mb-2">
                      <Calendar className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase tracking-wider">Días</span>
                    </div>
                    <span className="text-2xl font-bold text-white">
                      {result.days_remaining > 0 ? result.days_remaining : '0'}
                      <span className="text-sm text-[#6e6e73] font-normal ml-1">restantes</span>
                    </span>
                  </div>
                  {result.client.membership_end && (
                    <div className="p-4 rounded-xl bg-[#111113] border border-white/[0.04] col-span-2">
                      <div className="flex items-center gap-2 text-[#6e6e73] mb-2">
                        <Clock className="w-4 h-4" />
                        <span className="text-xs font-medium uppercase tracking-wider">Vencimiento</span>
                      </div>
                      <span className="text-base font-medium text-white">
                        {new Date(result.client.membership_end).toLocaleDateString('es-MX', {
                          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-4 border-t border-white/[0.06] flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[#6e6e73]">
                    <Activity className="w-4 h-4" />
                    <span className="text-sm">Entrada #{(result.client.access_history?.length || 0) + 1}</span>
                  </div>
                  <span className="text-sm text-[#48484d]">
                    {new Date(result.checked_in_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </main>

      <footer className="relative z-10 p-4 text-center">
        <p className="text-sm text-[#48484d]">
          {result || error ? 'La pantalla se reiniciará automáticamente' : 'Modo Recepción — Nova'}
        </p>
      </footer>
    </div>
  );
}
